/** Page-view tracking beacon hook.
 *
 * Fires on route change and page unload. Sends page_path, entered_at,
 * and exited_at to the POST /api/metrics/page-view endpoint.
 * Uses sendBeacon on unload for reliability; fetch for route changes.
 */

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { apiRequest } from "@/shared/lib/apiClient";

import type { PageViewPayload } from "@/domains/admin/lib/metricsTypes";

const EXCLUDED_PATHS = ["/admin/login", "/login", "/auth/callback", "/portal/auth/callback"];
const MIN_DWELL_SECONDS = 1;

export function usePageTracking(): void {
  const location = useLocation();
  const enteredAtRef = useRef<string | null>(null);
  const pathRef = useRef<string | null>(null);

  useEffect(() => {
    const now = new Date().toISOString();
    const currentPath = location.pathname;

    // Skip excluded paths (login, auth callbacks)
    if (EXCLUDED_PATHS.some((p) => currentPath.startsWith(p))) {
      enteredAtRef.current = null;
      pathRef.current = null;
      return;
    }

    // If we have a previous page, record its exit
    if (enteredAtRef.current && pathRef.current) {
      const enteredAt = new Date(enteredAtRef.current);
      const exitedAt = new Date(now);
      const dwellSeconds = (exitedAt.getTime() - enteredAt.getTime()) / 1000;

      if (dwellSeconds >= MIN_DWELL_SECONDS) {
        const payload: PageViewPayload = {
          page_path: pathRef.current,
          entered_at: enteredAtRef.current,
          exited_at: now,
        };
        // Fire-and-forget — don't block navigation
        apiRequest<unknown>("POST", "/api/metrics/page-view", { body: payload }).catch(() => {
          // Silently ignore tracking errors
        });
      }
    }

    // Track new page entry
    enteredAtRef.current = now;
    pathRef.current = currentPath;
  }, [location.pathname]);

  // Record exit on page unload using sendBeacon
  useEffect(() => {
    const handleUnload = () => {
      if (enteredAtRef.current && pathRef.current) {
        const payload: PageViewPayload = {
          page_path: pathRef.current,
          entered_at: enteredAtRef.current,
          exited_at: new Date().toISOString(),
        };
        const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
        navigator.sendBeacon("/api/metrics/page-view", blob);
      }
    };

    window.addEventListener("beforeunload", handleUnload);
    return () => {
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, []);
}
